import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MdOutlineInventory2 } from 'react-icons/md';
import { useAuthContext } from 'context/AuthContext';
import Header from 'components/Header';
import Button from 'components/Button';
import Tabs from 'components/Tabs';
import TextInput from 'components/TextInput';
import axios from 'config/axios';
import Info from './Info';
import Overview from './Overview';
import OverviewEmpty from './OverviewEmpty';
import Students from './Students';
import Tests from './Tests';
import Assignments from './Assignments';
import Dropdown3 from 'components/Dropdown3';
import VocabSets from './VocabSets';

import './index.css';
import Loader from 'components/Loader';

import Popup from 'components/Popup';
import Table from 'components/Table';

const TABS_BASE = ['Info', 'Class Data', 'Students', 'Tests', 'Assignments', 'Vocab'];
const TEST_TYPE = {'SAT': 'Digital SAT', 'ACT': 'Digital ACT', 'PSAT': 'Digital PSAT', 'SHSAT': 'Digital SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA' };
const TEST_TYPE_PAPER = {'SAT': 'Digital SAT', 'ACT': 'Digital ACT', 'PSAT': 'Digital PSAT', 'SHSAT': 'Digital SHSAT', 'TSIA2': 'TSIA2', 'BCA': 'BCA', 'Paper ACT': 'Paper ACT' };
const ASSIGNMENT_TYPES = ['Preset', 'Custom'];

function Class() {
    const [overviewData, setOverviewData] = useState(null);
    const [classInfo, setClassInfo] = useState(null);
    const [students, setStudents] = useState([]);
    const [tests, setTests] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [tabIndex, setTabIndex] = useState(1);
    const [testTabIndex, setTestTabIndex] = useState(0);
    const [updateStudents, setUpdateStudents] = useState(false);
    const [updateTests, setUpdateTests] = useState(false);
    const [updateAssignments, setUpdateAssignments] = useState(false);
    const [testType, setTestType] = useState(TEST_TYPE['SAT']);
    const [assignmentType, setAssignmentType] = useState(ASSIGNMENT_TYPES[0]);
    const [searchStudent, setSearchStudent] = useState(null);
    const [vocabQuizStatus, setVocabQuizStatus] = useState(null);
    const [vocabStudents, setVocabStudents] = useState([]);
    const [vocabError, setVocabError] = useState('');
    const [updateVocabSets, setUpdateVocabSets] = useState(false);
    const [archiveSelected, setArchiveSelected] = useState(false);

    const { user } = useAuthContext();
    const { classId } = useParams();

    const hasArchived = tests.some(test => test.archived);

    const TABS = user.customizations.find(customization => customization === 'Hide Vocab')
        ? TABS_BASE.filter(tab => tab !== 'Vocab')
        : TABS_BASE;

    const handleTestType = (value) => {
        setTestType(value);
    }

    const handleAssign = () => {
        setUpdateTests(false);
        setTestTabIndex(0);
        setTestType(TEST_TYPE['SAT']);
        setArchiveSelected(false);
    }

    const handleAssignmentAssign = () => {
        setUpdateAssignments(false);
    }

    useEffect(() => {
        axios.get(`/tutors/classes/${classId}/overview`)
        .then(res => {
            // console.log(res.data)
            setOverviewData(res.data)
        })
        .catch(error => {
            console.log(error);
        }); 
        axios.get(`/tutors/classes/${classId}`)
            .then(res => {
                setClassInfo(res.data.info);
                setStudents(res.data.students);
                setTests(res.data.tests);
                setAssignments(res.data.questionSets);
            })
            .catch(error => {
                console.log(error);
            }); 
    }, []);

    useEffect(() => {
        setUpdateStudents(false);
        setUpdateTests(false);
        setUpdateAssignments(false);
        setUpdateVocabSets(false);
        setArchiveSelected(false);
    }, [tabIndex]);

    const fetchVocabQuizStatus = () => {
        axios.get('vocab/tutor/classes/')
            .then(res => {
                const cls = res.data.classes.find(c => c.id === parseInt(classId));
                if (cls) setVocabQuizStatus(cls);
            })
            .catch(error => console.error(error));

        axios.get(`vocab/tutor/classes/${classId}/students/`)
            .then(res => setVocabStudents(res.data.students))
            .catch(error => console.error(error));
    };

    useEffect(() => {
        if (TABS[tabIndex] === 'Vocab') {
            fetchVocabQuizStatus();
        }
    }, [tabIndex]);

    const testTypes = user.customizations.find(customization => customization === 'Paper Test') ? TEST_TYPE_PAPER : TEST_TYPE;

    return (
        <div className='class'>
            <Header
                title={classInfo?.name ? classInfo.name : null}
                prevRoutes={[{ name: 'All Classes', path: '/classes' }]}
            />
            <div className='class__tabs'>
                {(!updateTests && !updateAssignments) &&
                    <Tabs
                        tabs={TABS}
                        tabIndex={tabIndex}
                        onSelect={TABS.map((_tab, t) => () => { setTabIndex(t) })}
                    />
                }
                {updateTests &&
                    <Tabs
                        tabs={[
                            ...Object.keys(testTypes).map(tab => (
                                <span className='tests__tabContent'>
                                    {tab} {tests.filter(test => test.type === testTypes[tab] && !test.archived).some(test => test.isNew) && (<span className='tests__newBadge'>NEW</span>)}
                                </span>
                            )),
                            ...(hasArchived ? [
                                <span className='tests__tabContent tests__tabContent--archive'>
                                    Archive
                                    <MdOutlineInventory2 className='tests__archiveTabIcon' />
                                </span>
                            ] : []),
                        ]}
                        tabIndex={testTabIndex}
                        onSelect={[
                            ...Object.values(testTypes).map((_tab, t) => (
                                () => {
                                    setTestTabIndex(t)
                                    setArchiveSelected(false)
                                    handleTestType(_tab)
                                }
                            )),
                            ...(hasArchived ? [
                                () => {
                                    setTestTabIndex(Object.keys(testTypes).length)
                                    setArchiveSelected(true)
                                }
                            ] : []),
                        ]}
                    />
                }
                {updateAssignments &&
                    <Tabs
                        tabs={ASSIGNMENT_TYPES.map(type => 
                            <span className="tests__tabContent">
                                {type}
                            </span>
                        )}
                        tabIndex={testTabIndex}
                        onSelect={ASSIGNMENT_TYPES.map((type, t) => (
                            () => {
                                setTestTabIndex(t)
                                setAssignmentType(type)
                            }
                        ))}
                    />
                }
                {user.role === 'Admin' && TABS[tabIndex] === 'Students' && (updateStudents
                    ?   <div className='class__tab'>
                            {searchStudent !== null
                            ?   <TextInput
                                    placeholder='search student'
                                    value={searchStudent}
                                    onChange={event => setSearchStudent(event.target.value)}
                                />
                            :   <div></div>
                            }
                            <Button secondary onClick={() => {
                                setUpdateStudents(false);
                                setSearchStudent(null);
                            }}>
                                Save
                            </Button>
                        </div>
                    :   <Button onClick={() => {
                            setUpdateStudents(true);
                            setSearchStudent('');
                        }}>
                            Add students
                        </Button>
                )}
                {TABS[tabIndex] === 'Tests' && (
                    <div className='class__buttons'>
                        {updateTests ? (
                            <Button secondary onClick={() => handleAssign()}>
                                Save
                            </Button>
                        ) : (
                            <Button onClick={() => setUpdateTests(true)}>
                                Assign
                            </Button>
                        )}
                    </div>
                )}
                {TABS[tabIndex] === 'Assignments' && (
                    <div className='class__buttons'>
                        {updateAssignments ? (
                            <Button secondary onClick={() => handleAssignmentAssign()}>
                                Save
                            </Button>
                        ) : (
                            <Button onClick={() => setUpdateAssignments(true)}>
                                Assign
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {(!classInfo || !overviewData)
            ?   <Loader></Loader>
            :   TABS[tabIndex] === 'Info'
            ?   <Info {...classInfo} setClassInfo={setClassInfo} />
            : TABS[tabIndex] === 'Class Data'
            ?  (overviewData.lastTestTestType
                ? <Overview
                    {...overviewData}
                    students={students}
                    tests={tests}
                    assignments={assignments}
                  />
                : <OverviewEmpty />)
            :   TABS[tabIndex] === 'Students'
            ?   <Students
                    students={searchStudent ? students.filter(student => (
                        student.inClass ||
                        student.email?.toLowerCase().includes(searchStudent.toLowerCase()) || 
                        student.studentId?.toLowerCase().includes(searchStudent.toLowerCase()) || 
                        student.firstName?.toLowerCase().includes(searchStudent.toLowerCase()) || 
                        student.lastName?.toLowerCase().includes(searchStudent.toLowerCase())
                    )) : students}
                    setStudents={setStudents}
                    updateStudents={updateStudents}
                />
            :   TABS[tabIndex] === 'Tests'
            ?   <Tests
                    studs={students}
                    tests={tests}
                    testType={testType}
                    setTests={setTests}
                    updateTests={updateTests}
                    archiveSelected={archiveSelected}
                />
            :   TABS[tabIndex] === 'Assignments'
            ?   <Assignments
                    studs={students}
                    assignments={assignments}
                    testType={'Digital SAT'}
                    assignmentType={assignmentType}
                    setAssignments={setAssignments}
                    updateAssignments={updateAssignments}
                />
            :   TABS[tabIndex] === 'Vocab'
            ?   <div className='class__vocab'>
                    <div className='class__vocabBar'>
                        <p className='class__vocabStatus'>
                            {vocabQuizStatus
                                ? <>{vocabQuizStatus.studentCount} students</>
                                : 'Loading...'
                            }
                        </p>
                        <div className='class__vocabButtons'>
                            {updateVocabSets ? (
                                <Button secondary onClick={() => setUpdateVocabSets(false)}>
                                    Done
                                </Button>
                            ) : (
                                <Button onClick={() => setUpdateVocabSets(true)}>
                                    Assign Sets
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Vocab sets assign/view */}
                    <VocabSets
                        vocabStudents={vocabStudents}
                        vocabQuizStatus={vocabQuizStatus}
                        updateVocabSets={updateVocabSets}
                    />

                    {/* Students table */}
                    {!updateVocabSets && (
                        <div className='class__vocabStudents'>
                            <h3 className='class__vocabStudentsTitle'>Students</h3>
                            <Table
                                header={['Name', 'Email']}
                                rows={vocabStudents.map(s => ([
                                    `${s.firstName} ${s.lastName}`,
                                    s.email,
                                ]))}
                            />
                        </div>
                    )}
                </div>
            :   <></>
            }
            {/* {!overviewData
            ? <></>
            : TABS[tabIndex] === 'Class Data'
            ?  (overviewData.lastTestTestType ? <Overview {...overviewData} /> : <OverviewEmpty />)
            : <></>
            } */}
        </div>
    );
}

export default Class;